/**
 * Website Templates
 *
 * Returns starter file sets (Record<path, content>) for each website template type.
 * All templates are vanilla HTML + Tailwind CDN — no build step required.
 */

export type WebsiteTemplate = 'page' | 'personal' | 'blog' | 'shop-river' | 'shop-shopify';

export function getWebsiteStarterFiles(
  name: string,
  template: WebsiteTemplate,
  description: string = ''
): Record<string, string> {
  switch (template) {
    case 'page':
      return pageTemplate(name, description);
    case 'personal':
      return personalTemplate(name, description);
    case 'blog':
      return blogTemplate(name, description);
    case 'shop-river':
      return shopRiverTemplate(name, description);
    case 'shop-shopify':
      return shopShopifyTemplate(name, description);
    default:
      return pageTemplate(name, description);
  }
}

// ---------------------------------------------------------------------------
// Single Page — minimal self-contained HTML/CSS/JS, no build step, no CDN
// ---------------------------------------------------------------------------

function pageTemplate(name: string, description: string): Record<string, string> {
  return {
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${name}</title>
  <meta name="description" content="${description || name}" />
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: #0f0f0f;
      color: #f0f0f0;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .container {
      text-align: center;
      padding: 2rem;
    }

    h1 {
      font-size: clamp(2rem, 8vw, 5rem);
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    p {
      margin-top: 1rem;
      font-size: 1.1rem;
      color: #888;
    }
  </style>
</head>
<body>
  <div class="container">
    <h1>${name}</h1>
    ${description ? `<p>${description}</p>` : ''}
  </div>
  <script>
    // Add interactivity here
  </script>
</body>
</html>`,
  };
}

// ---------------------------------------------------------------------------
// Personal / Portfolio
// ---------------------------------------------------------------------------

function personalTemplate(name: string, description: string): Record<string, string> {
  return {
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${name}</title>
  <meta name="description" content="${description || `Personal site for ${name}`}" />
  <meta property="og:title" content="${name}" />
  <meta property="og:description" content="${description || `Personal site for ${name}`}" />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <!-- Nav -->
  <nav class="fixed top-0 left-0 right-0 z-10 bg-white/80 backdrop-blur border-b border-gray-100">
    <div class="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
      <a href="#" data-nav="/" class="font-semibold text-gray-900">${name}</a>
      <div class="flex gap-6 text-sm text-gray-500">
        <a href="#about" class="hover:text-gray-900 transition-colors">About</a>
        <a href="#work" class="hover:text-gray-900 transition-colors">Work</a>
        <a href="#contact" class="hover:text-gray-900 transition-colors">Contact</a>
      </div>
    </div>
  </nav>

  <!-- Hero -->
  <header class="pt-32 pb-20 px-6">
    <div class="max-w-3xl mx-auto">
      <p class="text-sm text-indigo-600 font-medium mb-3">Hello, I'm</p>
      <h1 class="text-5xl font-bold tracking-tight mb-4">${name}</h1>
      <p class="text-xl text-gray-500 max-w-xl">
        ${description || 'Designer, developer, and creator of things.'}
      </p>
      <div class="mt-8 flex gap-4">
        <a href="#work" class="px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">
          View Work
        </a>
        <a href="#contact" class="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium hover:border-gray-400 transition-colors">
          Get in Touch
        </a>
      </div>
    </div>
  </header>

  <!-- About -->
  <section id="about" class="py-20 px-6 bg-gray-50">
    <div class="max-w-3xl mx-auto">
      <h2 class="text-2xl font-bold mb-8">About</h2>
      <div class="prose prose-gray max-w-none">
        <p class="text-gray-600 leading-relaxed text-lg">
          Write something about yourself here. Talk about your background, what you do, and what
          you're passionate about.
        </p>
        <p class="text-gray-600 leading-relaxed mt-4">
          Add a second paragraph with more detail.
        </p>
      </div>
    </div>
  </section>

  <!-- Work -->
  <section id="work" class="py-20 px-6">
    <div class="max-w-3xl mx-auto">
      <h2 class="text-2xl font-bold mb-8">Work</h2>
      <div class="grid gap-6">

        <!-- Project card (duplicate this for more projects) -->
        <div class="border border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-colors">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3 class="font-semibold text-lg mb-1">Project Name</h3>
              <p class="text-gray-500 text-sm">Brief description of the project and what you built.</p>
            </div>
            <span class="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded shrink-0">2024</span>
          </div>
          <div class="mt-4 flex gap-3">
            <a href="#" class="text-xs text-indigo-600 hover:underline">View Project</a>
            <a href="#" class="text-xs text-gray-400 hover:text-gray-600">GitHub</a>
          </div>
        </div>

        <div class="border border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-colors">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3 class="font-semibold text-lg mb-1">Another Project</h3>
              <p class="text-gray-500 text-sm">Brief description of the project and what you built.</p>
            </div>
            <span class="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded shrink-0">2023</span>
          </div>
          <div class="mt-4 flex gap-3">
            <a href="#" class="text-xs text-indigo-600 hover:underline">View Project</a>
          </div>
        </div>

      </div>
    </div>
  </section>

  <!-- Contact -->
  <section id="contact" class="py-20 px-6 bg-gray-50">
    <div class="max-w-3xl mx-auto">
      <h2 class="text-2xl font-bold mb-4">Get in Touch</h2>
      <p class="text-gray-500 mb-6">Have a project in mind or just want to say hi?</p>
      <a href="mailto:hello@example.com"
         class="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">
        Send me an email
      </a>
    </div>
  </section>

  <footer class="py-8 px-6 border-t border-gray-100">
    <div class="max-w-3xl mx-auto text-sm text-gray-400">
      © ${new Date().getFullYear()} ${name}
    </div>
  </footer>

  <script type="module">
    import { initNavigation } from './src/navigation.js';
    initNavigation();
  </script>

</body>
</html>`,

    'src/navigation.js': `/**
 * Navigation utility for static sites.
 * Converts data-nav attributes to proper relative paths.
 */

export function resolvePath(siteAbsolutePath) {
  let target = siteAbsolutePath.replace(/^\\/+/, '');
  if (target.endsWith('/') || target === '') {
    target = target + 'index.html';
  }
  
  const currentPath = window.location.pathname.replace(/^\\/+/, '');
  const currentNormalized = currentPath === '' || currentPath === '/' ? 'index.html' : currentPath;
  const currentParts = currentNormalized.split('/');
  currentParts.pop();
  const depth = currentParts.filter(p => p).length;
  
  if (depth === 0) return target;
  return '../'.repeat(depth) + target;
}

export function initNavigation() {
  document.querySelectorAll('[data-nav]').forEach(link => {
    const siteAbsolutePath = link.getAttribute('data-nav');
    if (siteAbsolutePath) link.href = resolvePath(siteAbsolutePath);
  });
}

export function navigateTo(siteAbsolutePath) {
  window.location.href = resolvePath(siteAbsolutePath);
}
`,

    'styles.css': `/* Custom styles for ${name} */
/* Tailwind is loaded via CDN — add overrides here */

:root {
  --color-accent: #6366f1;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
`,
  };
}

// ---------------------------------------------------------------------------
// Blog
// ---------------------------------------------------------------------------

function blogTemplate(name: string, description: string): Record<string, string> {
  const today = new Date().toISOString().split('T')[0];
  return {
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${name}</title>
  <meta name="description" content="${description || `${name} — a blog`}" />
  <meta property="og:title" content="${name}" />
  <meta property="og:description" content="${description || `${name} — a blog`}" />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
      <a href="#" data-nav="/" class="font-semibold">${name}</a>
      <a href="#" data-nav="/about.html" class="text-sm text-gray-500 hover:text-gray-900 transition-colors">About</a>
    </div>
  </nav>

  <main class="max-w-2xl mx-auto px-6 py-16">
    <h1 class="text-3xl font-bold mb-2">${name}</h1>
    <p class="text-gray-500 mb-12">${description || 'Writing about things I find interesting.'}</p>

    <div id="post-list" class="space-y-10">
      <!-- Posts are rendered by posts.js -->
    </div>
  </main>

  <footer class="border-t border-gray-100 mt-20 py-8 px-6">
    <div class="max-w-2xl mx-auto text-sm text-gray-400">
      © ${new Date().getFullYear()} ${name}
    </div>
  </footer>

  <script type="module">
    import { posts } from './src/posts.js';
    import { initNavigation, resolvePath } from './src/navigation.js';

    initNavigation();

    const list = document.getElementById('post-list');
    if (posts.length === 0) {
      list.innerHTML = '<p class="text-gray-400">No posts yet.</p>';
    } else {
      list.innerHTML = posts.map(p => \`
        <article>
          <time class="text-xs text-gray-400">\${new Date(p.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
          <h2 class="text-xl font-semibold mt-1 mb-2">
            <a href="\${resolvePath('/posts/' + p.slug + '.html')}" class="hover:text-indigo-600 transition-colors">\${p.title}</a>
          </h2>
          <p class="text-gray-500 text-sm leading-relaxed">\${p.excerpt}</p>
          <a href="\${resolvePath('/posts/' + p.slug + '.html')}" class="inline-block mt-3 text-xs text-indigo-600 hover:underline">Read more →</a>
        </article>
      \`).join('');
    }
  </script>

</body>
</html>`,

    'about.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>About | ${name}</title>
  <meta name="description" content="About ${name}" />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
      <a href="#" data-nav="/" class="font-semibold">${name}</a>
      <a href="#" data-nav="/about.html" class="text-sm text-indigo-600 font-medium">About</a>
    </div>
  </nav>

  <main class="max-w-2xl mx-auto px-6 py-16">
    <h1 class="text-3xl font-bold mb-8">About</h1>
    <p class="text-gray-600 leading-relaxed">
      Write about yourself here.
    </p>
  </main>

  <script type="module">
    import { initNavigation } from './src/navigation.js';
    initNavigation();
  </script>

</body>
</html>`,

    'posts/hello-world.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Hello World | ${name}</title>
  <meta name="description" content="My first post." />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="../styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-2xl mx-auto px-6 h-14 flex items-center">
      <a href="#" data-nav="/" class="font-semibold">${name}</a>
    </div>
  </nav>

  <main class="max-w-2xl mx-auto px-6 py-16">
    <time class="text-xs text-gray-400">${today}</time>
    <h1 class="text-3xl font-bold mt-2 mb-8">Hello World</h1>

    <div class="prose prose-gray">
      <p class="text-gray-600 leading-relaxed mb-4">
        This is the first post. Replace this content with your own writing.
      </p>
      <p class="text-gray-600 leading-relaxed">
        Add more paragraphs, headings, images, and code blocks here.
      </p>
    </div>

    <div class="mt-12 pt-8 border-t border-gray-100">
      <a href="#" data-nav="/" class="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Back to all posts</a>
    </div>
  </main>

  <script type="module">
    import { initNavigation } from '../src/navigation.js';
    initNavigation();
  </script>

</body>
</html>`,

    'src/posts.js': `/**
 * Posts manifest — edit this array to add/remove posts.
 * Add a matching HTML file in posts/ for each entry.
 */
export const posts = [
  {
    slug: 'hello-world',
    title: 'Hello World',
    date: '${today}',
    excerpt: 'This is the first post. Replace this content with your own writing.',
  },
];
`,

    'src/navigation.js': `/**
 * Navigation utility for static sites.
 * Converts data-nav attributes to proper relative paths.
 */

export function resolvePath(siteAbsolutePath) {
  let target = siteAbsolutePath.replace(/^\\/+/, '');
  if (target.endsWith('/') || target === '') {
    target = target + 'index.html';
  }
  
  const currentPath = window.location.pathname.replace(/^\\/+/, '');
  const currentNormalized = currentPath === '' || currentPath === '/' ? 'index.html' : currentPath;
  const currentParts = currentNormalized.split('/');
  currentParts.pop();
  const depth = currentParts.filter(p => p).length;
  
  if (depth === 0) return target;
  return '../'.repeat(depth) + target;
}

export function initNavigation() {
  document.querySelectorAll('[data-nav]').forEach(link => {
    const siteAbsolutePath = link.getAttribute('data-nav');
    if (siteAbsolutePath) link.href = resolvePath(siteAbsolutePath);
  });
}

export function navigateTo(siteAbsolutePath) {
  window.location.href = resolvePath(siteAbsolutePath);
}
`,

    'styles.css': `/* Custom styles for ${name} */
html { scroll-behavior: smooth; }
body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
`,
  };
}

// ---------------------------------------------------------------------------
// Shop — River + Stripe
// ---------------------------------------------------------------------------

function shopRiverTemplate(name: string, description: string): Record<string, string> {
  return {
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${name}</title>
  <meta name="description" content="${description || name}" />
  <meta property="og:title" content="${name}" />
  <meta property="og:description" content="${description || name}" />
  <script src="https://cdn.tailwindcss.com"></script>
  <script src="https://js.stripe.com/v3/"></script>
  <link rel="stylesheet" href="styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <div class="flex items-center gap-6">
        <a href="#" data-nav="/shop/" class="text-sm text-gray-600 hover:text-gray-900">Shop</a>
        <a href="#" data-nav="/cart/" class="relative text-sm font-medium">
          Cart
          <span id="cart-count" class="ml-1 text-xs bg-gray-900 text-white rounded-full px-1.5 py-0.5 hidden">0</span>
        </a>
      </div>
    </div>
  </nav>

  <header class="py-24 px-6 text-center bg-gray-50">
    <h1 class="text-5xl font-bold mb-4">${name}</h1>
    <p class="text-xl text-gray-500 mb-8">${description || 'Quality products, shipped fast.'}</p>
    <a href="#" data-nav="/shop/"
       class="inline-block px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors">
      Shop Now
    </a>
  </header>

  <section class="py-20 px-6">
    <div class="max-w-5xl mx-auto">
      <h2 class="text-2xl font-bold mb-10">Featured Products</h2>
      <div id="featured-products" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <p class="text-gray-400 col-span-3">Loading products...</p>
      </div>
    </div>
  </section>

  <footer class="border-t border-gray-100 py-8 px-6 mt-12">
    <div class="max-w-5xl mx-auto text-sm text-gray-400">
      © ${new Date().getFullYear()} ${name}
    </div>
  </footer>

  <script type="module">
    import { RiverShop, formatPrice } from './src/river-shop.js';
    import { updateCartCount } from './src/cart.js';
    import { initNavigation, resolvePath } from './src/navigation.js';

    initNavigation();
    updateCartCount();

    const container = document.getElementById('featured-products');
    try {
      const products = await RiverShop.getProducts();
      if (products.length === 0) {
        container.innerHTML = '<p class="text-gray-400 col-span-3">No products yet. Add some Product entities to get started.</p>';
      } else {
        container.innerHTML = products.slice(0, 3).map(p => \`
          <a href="\${resolvePath('/shop/product.html')}?id=\${p._id}" class="group border border-gray-200 rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
            <div class="aspect-square bg-gray-50 overflow-hidden">
              <img src="\${p.skeleton?.imageUrl || 'https://placehold.co/400x400'}"
                   alt="\${p.name}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
            </div>
            <div class="p-4">
              <h3 class="font-medium">\${p.name}</h3>
              <p class="text-gray-500 text-sm mt-1">\${formatPrice(p.skeleton?.price, p.skeleton?.currency)}</p>
            </div>
          </a>
        \`).join('');
      }
    } catch (err) {
      container.innerHTML = \`<p class="text-red-500 col-span-3">Error: \${err.message}</p>\`;
    }
  </script>

</body>
</html>`,

    'shop/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Shop | ${name}</title>
  <meta name="description" content="Browse all products from ${name}." />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="../styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <a href="#" data-nav="/cart/" class="text-sm font-medium">
        Cart <span id="cart-count" class="ml-1 text-xs bg-gray-900 text-white rounded-full px-1.5 py-0.5 hidden">0</span>
      </a>
    </div>
  </nav>

  <main class="max-w-5xl mx-auto px-6 py-12">
    <h1 class="text-3xl font-bold mb-10">All Products</h1>
    <div id="products" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <p class="text-gray-400 col-span-3">Loading...</p>
    </div>
  </main>

  <script type="module">
    import { RiverShop, formatPrice } from '../src/river-shop.js';
    import { updateCartCount } from '../src/cart.js';
    import { initNavigation, resolvePath } from '../src/navigation.js';

    initNavigation();
    updateCartCount();

    const container = document.getElementById('products');
    try {
      const products = await RiverShop.getProducts();
      if (products.length === 0) {
        container.innerHTML = '<p class="text-gray-400 col-span-3">No products yet.</p>';
      } else {
        container.innerHTML = products.map(p => \`
          <a href="\${resolvePath('/shop/product.html')}?id=\${p._id}" class="group border border-gray-200 rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
            <div class="aspect-square bg-gray-50">
              <img src="\${p.skeleton?.imageUrl || 'https://placehold.co/400x400'}"
                   alt="\${p.name}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
            </div>
            <div class="p-4">
              <h3 class="font-medium">\${p.name}</h3>
              <p class="text-sm text-gray-500 mt-1 line-clamp-2">\${p.skeleton?.description || ''}</p>
              <p class="font-semibold mt-2">\${formatPrice(p.skeleton?.price, p.skeleton?.currency)}</p>
            </div>
          </a>
        \`).join('');
      }
    } catch (err) {
      container.innerHTML = \`<p class="text-red-500 col-span-3">Error: \${err.message}</p>\`;
    }
  </script>

</body>
</html>`,

    'shop/product.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Product | ${name}</title>
  <meta name="description" content="Product details." />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="../styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <a href="#" data-nav="/cart/" class="text-sm font-medium">
        Cart <span id="cart-count" class="ml-1 text-xs bg-gray-900 text-white rounded-full px-1.5 py-0.5 hidden">0</span>
      </a>
    </div>
  </nav>

  <main class="max-w-5xl mx-auto px-6 py-12">
    <a href="#" data-nav="/shop/" class="text-sm text-gray-400 hover:text-gray-600 mb-8 inline-block">← Back to shop</a>
    <div id="product" class="text-gray-400">Loading...</div>
  </main>

  <script type="module">
    import { RiverShop, formatPrice } from '../src/river-shop.js';
    import { addToCart, updateCartCount } from '../src/cart.js';
    import { initNavigation, resolvePath } from '../src/navigation.js';

    initNavigation();
    updateCartCount();

    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    const container = document.getElementById('product');

    if (!id) {
      container.innerHTML = '<p class="text-red-500">No product ID in URL.</p>';
    } else {
      try {
        const p = await RiverShop.getProduct(id);
        if (!p) { container.innerHTML = '<p class="text-red-500">Product not found.</p>'; }
        else {
          document.title = \`\${p.name} | ${name}\`;
          container.innerHTML = \`
            <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div class="aspect-square bg-gray-50 rounded-xl overflow-hidden">
                <img src="\${p.skeleton?.imageUrl || 'https://placehold.co/600x600'}"
                     alt="\${p.name}" class="w-full h-full object-cover" />
              </div>
              <div class="flex flex-col justify-center">
                <h1 class="text-3xl font-bold mb-3">\${p.name}</h1>
                <p class="text-2xl font-semibold mb-4">\${formatPrice(p.skeleton?.price, p.skeleton?.currency)}</p>
                <p class="text-gray-500 leading-relaxed mb-8">\${p.skeleton?.description || ''}</p>
                <button id="add-to-cart"
                  class="px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors \${!p.skeleton?.inStock ? 'opacity-50 cursor-not-allowed' : ''}"
                  \${!p.skeleton?.inStock ? 'disabled' : ''}>
                  \${p.skeleton?.inStock !== false ? 'Add to Cart' : 'Out of Stock'}
                </button>
                <p id="add-confirm" class="mt-3 text-sm text-green-600 hidden">Added to cart!</p>
              </div>
            </div>
          \`;
          document.getElementById('add-to-cart')?.addEventListener('click', () => {
            addToCart({ id: p._id, name: p.name, price: p.skeleton?.price, currency: p.skeleton?.currency, imageUrl: p.skeleton?.imageUrl, stripePriceId: p.skeleton?.stripePriceId });
            updateCartCount();
            const confirm = document.getElementById('add-confirm');
            if (confirm) { confirm.classList.remove('hidden'); setTimeout(() => confirm.classList.add('hidden'), 2000); }
          });
        }
      } catch (err) {
        container.innerHTML = \`<p class="text-red-500">Error: \${err.message}</p>\`;
      }
    }
  </script>

</body>
</html>`,

    'cart/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Cart | ${name}</title>
  <meta name="description" content="Your shopping cart." />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="../styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <a href="#" data-nav="/shop/" class="text-sm text-gray-400 hover:text-gray-600">Continue Shopping</a>
    </div>
  </nav>

  <main class="max-w-3xl mx-auto px-6 py-12">
    <h1 class="text-3xl font-bold mb-10">Your Cart</h1>
    <div id="cart-items"></div>
    <div id="cart-summary" class="hidden mt-8 pt-8 border-t border-gray-100">
      <div class="flex justify-between text-lg font-semibold mb-6">
        <span>Total</span>
        <span id="cart-total"></span>
      </div>
      <button id="checkout-btn"
        class="w-full py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors">
        Checkout with Stripe
      </button>
      <p class="text-xs text-gray-400 text-center mt-3">Secure checkout powered by Stripe</p>
    </div>
  </main>

  <script type="module">
    import { getCart, removeFromCart, formatPrice } from '../src/cart.js';
    import { StripeCheckout } from '../src/stripe-checkout.js';
    import { initNavigation, resolvePath } from '../src/navigation.js';

    initNavigation();

    function render() {
      const cart = getCart();
      const container = document.getElementById('cart-items');
      const summary = document.getElementById('cart-summary');

      if (cart.length === 0) {
        container.innerHTML = \`
          <div class="text-center py-20">
            <p class="text-gray-400 mb-4">Your cart is empty.</p>
            <a href="\${resolvePath('/shop/')}" class="text-indigo-600 hover:underline">Continue shopping →</a>
          </div>
        \`;
        summary?.classList.add('hidden');
      } else {
        container.innerHTML = cart.map(item => \`
          <div class="flex items-center gap-4 py-4 border-b border-gray-100" data-id="\${item.id}">
            <img src="\${item.imageUrl || 'https://placehold.co/80x80'}" alt="\${item.name}"
                 class="w-20 h-20 object-cover rounded-lg bg-gray-50" />
            <div class="flex-1">
              <p class="font-medium">\${item.name}</p>
              <p class="text-sm text-gray-500 mt-1">\${formatPrice(item.price, item.currency)} × \${item.quantity}</p>
            </div>
            <button class="remove-btn text-xs text-gray-400 hover:text-red-500 transition-colors" data-id="\${item.id}">Remove</button>
          </div>
        \`).join('');

        const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const currency = cart[0]?.currency || 'usd';
        const totalEl = document.getElementById('cart-total');
        if (totalEl) totalEl.textContent = formatPrice(total, currency);
        summary?.classList.remove('hidden');

        container.querySelectorAll('.remove-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            removeFromCart(btn.dataset.id);
            render();
          });
        });
      }
    }

    document.getElementById('checkout-btn')?.addEventListener('click', async () => {
      const cart = getCart();
      const items = cart.filter(i => i.stripePriceId).map(i => ({ priceId: i.stripePriceId, quantity: i.quantity }));
      if (items.length === 0) {
        alert('No items have a Stripe Price ID. Set stripePriceId on each Product entity first.');
        return;
      }
      const btn = document.getElementById('checkout-btn');
      if (btn) btn.textContent = 'Redirecting to Stripe...';
      try {
        await StripeCheckout.checkout(items);
      } catch (err) {
        alert('Checkout error: ' + err.message);
        if (btn) btn.textContent = 'Checkout with Stripe';
      }
    });

    render();
  </script>

</body>
</html>`,

    'success.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Order Confirmed | ${name}</title>
  <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-white text-gray-900 antialiased min-h-screen flex items-center justify-center">
  <div class="text-center px-6">
    <div class="text-5xl mb-6">✅</div>
    <h1 class="text-3xl font-bold mb-3">Order Confirmed!</h1>
    <p class="text-gray-500 mb-8">Thanks for your purchase. You'll receive a confirmation email shortly.</p>
    <a href="#" data-nav="/" class="px-6 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">
      Back to Home
    </a>
    <p id="popup-notice" class="mt-4 text-sm text-gray-400 hidden">You can close this window.</p>
  </div>
  <script type="module">
    import { initNavigation } from './src/navigation.js';
    initNavigation();
    
    // Clear cart
    localStorage.removeItem('cart');
    
    // If we're in a popup (opened by checkout), notify the opener and close
    const isPopup = window.opener && window.opener !== window;
    
    if (isPopup) {
      console.log('[Success] In popup mode, notifying opener');
      
      // Show popup-specific message
      document.getElementById('popup-notice')?.classList.remove('hidden');
      
      // Notify opener window that checkout succeeded
      try {
        window.opener.postMessage({
          type: 'checkout-success',
          sessionId: new URLSearchParams(window.location.search).get('session_id')
        }, '*');
        
        console.log('[Success] Sent checkout-success to opener');
        
        // Auto-close after 3 seconds
        setTimeout(() => {
          console.log('[Success] Auto-closing popup');
          window.close();
        }, 3000);
      } catch (err) {
        console.error('[Success] Failed to notify opener:', err);
      }
    }
  </script>
</body>
</html>`,

    'styles.css': `/* Custom styles for ${name} */
html { scroll-behavior: smooth; }
body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
`,

    'src/navigation.js': `/**
 * Navigation — robust path resolution for static sites.
 * 
 * CRITICAL: Always use these helpers for ALL navigation links.
 * Never use hardcoded absolute paths like "/shop/index.html".
 * 
 * Usage in HTML:
 *   <a href="#" data-nav="/shop/">Shop</a>
 *   <a href="#" data-nav="/">Home</a>
 * 
 * Usage in JavaScript (for dynamic links):
 *   import { resolvePath } from './navigation.js';
 *   const url = resolvePath('/shop/product.html') + '?id=123';
 * 
 * Call initNavigation() on every page to activate data-nav links.
 */

/**
 * Resolve a site-absolute path (like "/shop/") to a relative path from current page.
 * 
 * Site paths use "/" as site root and always end in "/" for directories:
 *   "/" → index.html at root
 *   "/shop/" → shop/index.html
 *   "/cart/" → cart/index.html
 *   "/shop/product.html" → shop/product.html (explicit file)
 * 
 * Examples from cart/index.html:
 *   resolvePath("/")              → "../index.html"
 *   resolvePath("/shop/")         → "../shop/index.html"
 *   resolvePath("/shop/product.html") → "../shop/product.html"
 * 
 * Examples from index.html:
 *   resolvePath("/shop/")         → "shop/index.html"
 *   resolvePath("/cart/")         → "cart/index.html"
 */
export function resolvePath(siteAbsolutePath) {
  // Normalize: /shop/ → shop/index.html, / → index.html
  let target = siteAbsolutePath.replace(/^\\/+/, '');
  if (target.endsWith('/') || target === '') {
    target = target + 'index.html';
  }
  
  // Get current page path relative to site root
  const currentPath = window.location.pathname.replace(/^\\/+/, '');
  
  // Handle special case: if we're at bare "/" or empty, treat as index.html
  const currentNormalized = currentPath === '' || currentPath === '/' ? 'index.html' : currentPath;
  
  // Calculate directory depth of current page
  // index.html → 0, shop/index.html → 1, shop/category/page.html → 2
  const currentParts = currentNormalized.split('/');
  currentParts.pop(); // Remove filename, keep directory parts
  const depth = currentParts.filter(p => p).length;
  
  // Build relative path
  if (depth === 0) {
    // We're at root level
    return target;
  } else {
    // We're in a subdirectory, use ../ to go up
    const upPath = '../'.repeat(depth);
    return upPath + target;
  }
}

/**
 * Initialize navigation by converting all data-nav attributes to proper hrefs.
 * Call this in <script type="module"> at the bottom of every page.
 */
export function initNavigation() {
  document.querySelectorAll('[data-nav]').forEach(link => {
    const siteAbsolutePath = link.getAttribute('data-nav');
    if (siteAbsolutePath) {
      link.href = resolvePath(siteAbsolutePath);
    }
  });
}

/**
 * Navigate programmatically (for dynamic navigation in JS).
 */
export function navigateTo(siteAbsolutePath) {
  window.location.href = resolvePath(siteAbsolutePath);
}
`,

    'src/river-shop.js': `/**
 * River Shop — reads Product entities via the River API bridge.
 *
 * Products are River entities with entityType 'Product'.
 * Required skeleton fields:
 *   price (number, in cents — e.g. 2500 = $25.00)
 *   currency (string — 'usd', 'eur', etc.)
 *   description (string)
 *   imageUrl (string)
 *   inStock (boolean)
 *   inventory (number | null — null = unlimited)
 *   stripePriceId (string — set this after creating a price in Stripe Dashboard)
 *
 * To create products, ask the AI to use river.entity.create with entityType 'Product'.
 */

export const RiverShop = {
  async getProducts() {
    // window.river is the PostMessage bridge injected by the Builder preview
    if (!window.river) throw new Error('River API not available. This site must be opened in the River Builder preview.');
    return window.river.entity.query({ entityType: 'Product' });
  },

  async getProduct(id) {
    if (!window.river) throw new Error('River API not available.');
    return window.river.entity.get(id);
  },
};

export function formatPrice(cents, currency = 'usd') {
  if (cents == null) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}
`,

    'src/cart.js': `/**
 * Cart — localStorage-backed shopping cart.
 */

const CART_KEY = 'cart';

export function getCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY) || '[]');
  } catch {
    return [];
  }
}

export function addToCart(item) {
  const cart = getCart();
  const existing = cart.find(i => i.id === item.id);
  if (existing) {
    existing.quantity = (existing.quantity || 1) + 1;
  } else {
    cart.push({ ...item, quantity: 1 });
  }
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
}

export function removeFromCart(id) {
  const cart = getCart().filter(i => i.id !== id);
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
}

export function clearCart() {
  localStorage.removeItem(CART_KEY);
}

export function updateCartCount() {
  const count = getCart().reduce((sum, item) => sum + (item.quantity || 1), 0);
  document.querySelectorAll('#cart-count').forEach(el => {
    if (count > 0) {
      el.textContent = String(count);
      el.classList.remove('hidden');
    } else {
      el.classList.add('hidden');
    }
  });
}

export function formatPrice(cents, currency = 'usd') {
  if (cents == null) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}
`,

    'src/stripe-checkout.js': `/**
 * Stripe Checkout — posts cart items to the River backend checkout endpoint.
 *
 * The backend route /api/website/checkout creates a Stripe Checkout Session
 * and returns a redirect URL. The user must set STRIPE_SECRET_KEY in their
 * deployment environment.
 *
 * Each item must have a stripePriceId (set on the River Product entity).
 * 
 * NOTE: Stripe Checkout blocks iframe usage. In preview mode, checkout opens
 * in a new window. In production, it redirects normally.
 */

export const StripeCheckout = {
  async checkout(items) {
    const res = await fetch('/api/website/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || \`Checkout failed: \${res.status}\`);
    }

    const { url } = await res.json();
    
    // Detect if we're in an iframe (preview mode)
    const inIframe = window.self !== window.top;
    
    if (inIframe) {
      // Preview mode: open checkout in new window
      // Stripe blocks iframe usage for security
      const checkoutWindow = window.open(url, '_blank', 'width=800,height=900');
      if (!checkoutWindow) {
        throw new Error('Please allow popups to complete checkout in preview mode');
      }
      
      // Listen for success message from popup
      window.addEventListener('message', (e) => {
        if (e.data.type === 'checkout-success') {
          console.log('[Checkout] Received success notification from popup');
          
          // Clear cart and show success message
          localStorage.removeItem('cart');
          
          // Reload page to show empty cart
          window.location.reload();
        }
      });
      
      // Also monitor popup closure
      const checkClosed = setInterval(() => {
        if (checkoutWindow.closed) {
          clearInterval(checkClosed);
          console.log('[Checkout] Popup closed, reloading to refresh cart');
          // Reload to update cart count (user may have completed purchase)
          window.location.reload();
        }
      }, 1000);
    } else {
      // Production: redirect normally
      window.location.href = url;
    }
  },
};
`,
  };
}

// ---------------------------------------------------------------------------
// Shop — Shopify Storefront API
// ---------------------------------------------------------------------------

function shopShopifyTemplate(name: string, description: string): Record<string, string> {
  return {
    'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${name}</title>
  <meta name="description" content="${description || name}" />
  <meta property="og:title" content="${name}" />
  <meta property="og:description" content="${description || name}" />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <div class="flex items-center gap-6">
        <a href="#" data-nav="/shop/" class="text-sm text-gray-600 hover:text-gray-900">Shop</a>
        <a href="#" data-nav="/cart/" class="text-sm font-medium">
          Cart <span id="cart-count" class="ml-1 text-xs bg-gray-900 text-white rounded-full px-1.5 py-0.5 hidden">0</span>
        </a>
      </div>
    </div>
  </nav>

  <header class="py-24 px-6 text-center bg-gray-50">
    <h1 class="text-5xl font-bold mb-4">${name}</h1>
    <p class="text-xl text-gray-500 mb-8">${description || 'Quality products, shipped fast.'}</p>
    <a href="#" data-nav="/shop/" class="inline-block px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors">
      Shop Now
    </a>
  </header>

  <section class="py-20 px-6">
    <div class="max-w-5xl mx-auto">
      <h2 class="text-2xl font-bold mb-10">Featured Products</h2>
      <div id="featured-products" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <p class="text-gray-400 col-span-3">Loading products...</p>
      </div>
    </div>
  </section>

  <footer class="border-t border-gray-100 py-8 px-6 mt-12">
    <div class="max-w-5xl mx-auto text-sm text-gray-400">
      © ${new Date().getFullYear()} ${name}
    </div>
  </footer>

  <script type="module">
    import { getProducts, formatPrice } from './src/shopify.js';
    import { updateCartCount } from './src/cart.js';
    import { initNavigation, resolvePath } from './src/navigation.js';

    initNavigation();
    updateCartCount();

    const container = document.getElementById('featured-products');
    try {
      const products = await getProducts(3);
      if (!products.length) {
        container.innerHTML = '<p class="text-gray-400 col-span-3">No products found. Check your Shopify config in src/shopify-config.js.</p>';
      } else {
        container.innerHTML = products.map(p => {
          const price = p.priceRange?.minVariantPrice;
          const img = p.featuredImage?.url || 'https://placehold.co/400x400';
          return \`
            <a href="\${resolvePath('/shop/product.html')}?handle=\${p.handle}" class="group border border-gray-200 rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
              <div class="aspect-square bg-gray-50">
                <img src="\${img}" alt="\${p.title}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div class="p-4">
                <h3 class="font-medium">\${p.title}</h3>
                <p class="text-sm text-gray-500 mt-1">\${price ? formatPrice(price.amount, price.currencyCode) : ''}</p>
              </div>
            </a>
          \`;
        }).join('');
      }
    } catch (err) {
      container.innerHTML = \`<p class="text-red-500 col-span-3">Error: \${err.message}</p>\`;
    }
  </script>

</body>
</html>`,

    'shop/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Shop | ${name}</title>
  <meta name="description" content="Browse all products." />
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="../styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <a href="#" data-nav="/cart/" class="text-sm font-medium">
        Cart <span id="cart-count" class="ml-1 text-xs bg-gray-900 text-white rounded-full px-1.5 py-0.5 hidden">0</span>
      </a>
    </div>
  </nav>

  <main class="max-w-5xl mx-auto px-6 py-12">
    <h1 class="text-3xl font-bold mb-10">All Products</h1>
    <div id="products" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <p class="text-gray-400 col-span-3">Loading...</p>
    </div>
  </main>

  <script type="module">
    import { getProducts, formatPrice } from '../src/shopify.js';
    import { updateCartCount } from '../src/cart.js';
    import { initNavigation, resolvePath } from '../src/navigation.js';

    initNavigation();
    updateCartCount();

    const container = document.getElementById('products');
    try {
      const products = await getProducts(24);
      if (!products.length) {
        container.innerHTML = '<p class="text-gray-400 col-span-3">No products found.</p>';
      } else {
        container.innerHTML = products.map(p => {
          const price = p.priceRange?.minVariantPrice;
          const img = p.featuredImage?.url || 'https://placehold.co/400x400';
          return \`
            <a href="\${resolvePath('/shop/product.html')}?handle=\${p.handle}" class="group border border-gray-200 rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
              <div class="aspect-square bg-gray-50">
                <img src="\${img}" alt="\${p.title}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div class="p-4">
                <h3 class="font-medium">\${p.title}</h3>
                <p class="font-semibold mt-2">\${price ? formatPrice(price.amount, price.currencyCode) : ''}</p>
              </div>
            </a>
          \`;
        }).join('');
      }
    } catch (err) {
      container.innerHTML = \`<p class="text-red-500 col-span-3">Error: \${err.message}</p>\`;
    }
  </script>

</body>
</html>`,

    'shop/product.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Product | ${name}</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="../styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <a href="#" data-nav="/cart/" class="text-sm font-medium">
        Cart <span id="cart-count" class="ml-1 text-xs bg-gray-900 text-white rounded-full px-1.5 py-0.5 hidden">0</span>
      </a>
    </div>
  </nav>

  <main class="max-w-5xl mx-auto px-6 py-12">
    <a href="#" data-nav="/shop/" class="text-sm text-gray-400 hover:text-gray-600 mb-8 inline-block">← Back to shop</a>
    <div id="product" class="text-gray-400">Loading...</div>
  </main>

  <script type="module">
    import { getProductByHandle, formatPrice } from '../src/shopify.js';
    import { addToCart, updateCartCount } from '../src/cart.js';
    import { initNavigation, resolvePath } from '../src/navigation.js';

    initNavigation();
    updateCartCount();

    const params = new URLSearchParams(window.location.search);
    const handle = params.get('handle');
    const container = document.getElementById('product');

    if (!handle) {
      container.innerHTML = '<p class="text-red-500">No product handle in URL.</p>';
    } else {
      try {
        const p = await getProductByHandle(handle);
        if (!p) { container.innerHTML = '<p class="text-red-500">Product not found.</p>'; }
        else {
          document.title = \`\${p.title} | ${name}\`;
          const firstVariant = p.variants?.edges?.[0]?.node;
          const images = p.images?.edges?.map(e => e.node) || [];
          const mainImg = images[0]?.url || 'https://placehold.co/600x600';
          const price = firstVariant?.price ? formatPrice(firstVariant.price.amount, firstVariant.price.currencyCode) : '';

          container.innerHTML = \`
            <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div class="aspect-square bg-gray-50 rounded-xl overflow-hidden">
                <img src="\${mainImg}" alt="\${p.title}" class="w-full h-full object-cover" />
              </div>
              <div class="flex flex-col justify-center">
                <h1 class="text-3xl font-bold mb-3">\${p.title}</h1>
                <p class="text-2xl font-semibold mb-4">\${price}</p>
                <div class="text-gray-500 leading-relaxed mb-8 text-sm" id="desc"></div>
                <button id="add-to-cart"
                  class="px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors \${firstVariant?.availableForSale === false ? 'opacity-50 cursor-not-allowed' : ''}"
                  \${firstVariant?.availableForSale === false ? 'disabled' : ''}>
                  \${firstVariant?.availableForSale !== false ? 'Add to Cart' : 'Out of Stock'}
                </button>
                <p id="add-confirm" class="mt-3 text-sm text-green-600 hidden">Added to cart!</p>
              </div>
            </div>
          \`;

          // Render HTML description safely
          const descEl = document.getElementById('desc');
          if (descEl) descEl.innerHTML = p.descriptionHtml || '';

          document.getElementById('add-to-cart')?.addEventListener('click', () => {
            if (!firstVariant) return;
            addToCart({ id: firstVariant.id, name: p.title, price: Math.round(parseFloat(firstVariant.price?.amount || '0') * 100), currency: firstVariant.price?.currencyCode || 'USD', imageUrl: mainImg, variantId: firstVariant.id });
            updateCartCount();
            const c = document.getElementById('add-confirm');
            if (c) { c.classList.remove('hidden'); setTimeout(() => c.classList.add('hidden'), 2000); }
          });
        }
      } catch (err) {
        container.innerHTML = \`<p class="text-red-500">Error: \${err.message}</p>\`;
      }
    }
  </script>

</body>
</html>`,

    'cart/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Cart | ${name}</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="stylesheet" href="../styles.css" />
</head>
<body class="bg-white text-gray-900 antialiased">

  <nav class="border-b border-gray-100">
    <div class="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
      <a href="#" data-nav="/" class="text-xl font-bold">${name}</a>
      <a href="#" data-nav="/shop/" class="text-sm text-gray-400 hover:text-gray-600">Continue Shopping</a>
    </div>
  </nav>

  <main class="max-w-3xl mx-auto px-6 py-12">
    <h1 class="text-3xl font-bold mb-10">Your Cart</h1>
    <div id="cart-items"></div>
    <div id="cart-summary" class="hidden mt-8 pt-8 border-t border-gray-100">
      <div class="flex justify-between text-lg font-semibold mb-6">
        <span>Total</span>
        <span id="cart-total"></span>
      </div>
      <button id="checkout-btn"
        class="w-full py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors">
        Checkout via Shopify
      </button>
      <p class="text-xs text-gray-400 text-center mt-3">You'll be redirected to Shopify's secure checkout.</p>
    </div>
  </main>

  <script type="module">
    import { createCheckout } from '../src/shopify.js';
    import { getCart, removeFromCart, formatPrice } from '../src/cart.js';
    import { initNavigation, resolvePath } from '../src/navigation.js';

    initNavigation();

    function render() {
      const cart = getCart();
      const container = document.getElementById('cart-items');
      const summary = document.getElementById('cart-summary');

      if (cart.length === 0) {
        container.innerHTML = \`<div class="text-center py-20"><p class="text-gray-400 mb-4">Your cart is empty.</p><a href="\${resolvePath('/shop/')}" class="text-indigo-600 hover:underline">Continue shopping →</a></div>\`;
        summary?.classList.add('hidden');
      } else {
        container.innerHTML = cart.map(item => \`
          <div class="flex items-center gap-4 py-4 border-b border-gray-100">
            <img src="\${item.imageUrl || 'https://placehold.co/80x80'}" alt="\${item.name}" class="w-20 h-20 object-cover rounded-lg bg-gray-50" />
            <div class="flex-1">
              <p class="font-medium">\${item.name}</p>
              <p class="text-sm text-gray-500 mt-1">\${formatPrice(item.price, item.currency)} × \${item.quantity}</p>
            </div>
            <button class="remove-btn text-xs text-gray-400 hover:text-red-500 transition-colors" data-id="\${item.id}">Remove</button>
          </div>
        \`).join('');

        const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const currency = cart[0]?.currency || 'usd';
        const totalEl = document.getElementById('cart-total');
        if (totalEl) totalEl.textContent = formatPrice(total, currency);
        summary?.classList.remove('hidden');

        container.querySelectorAll('.remove-btn').forEach(btn => {
          btn.addEventListener('click', () => { removeFromCart(btn.dataset.id); render(); });
        });
      }
    }

    document.getElementById('checkout-btn')?.addEventListener('click', async () => {
      const cart = getCart();
      const lineItems = cart.filter(i => i.variantId).map(i => ({ variantId: i.variantId, quantity: i.quantity }));
      if (!lineItems.length) { alert('Cart items are missing Shopify variant IDs.'); return; }
      const btn = document.getElementById('checkout-btn');
      if (btn) btn.textContent = 'Redirecting to Shopify...';
      try {
        const url = await createCheckout(lineItems);
        window.location.href = url;
      } catch (err) {
        alert('Checkout error: ' + err.message);
        if (btn) btn.textContent = 'Checkout via Shopify';
      }
    });

    render();
  </script>

</body>
</html>`,

    'success.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Order Confirmed | ${name}</title>
  <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-white text-gray-900 antialiased min-h-screen flex items-center justify-center">
  <div class="text-center px-6">
    <div class="text-5xl mb-6">✅</div>
    <h1 class="text-3xl font-bold mb-3">Order Confirmed!</h1>
    <p class="text-gray-500 mb-8">Thanks for your purchase.</p>
    <a href="#" data-nav="/" class="px-6 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">Back to Home</a>
  </div>
  <script type="module">
    import { initNavigation } from './src/navigation.js';
    initNavigation();
    localStorage.removeItem('cart');
  </script>
</body>
</html>`,

    'styles.css': `/* Custom styles for ${name} */
html { scroll-behavior: smooth; }
body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
`,

    'src/shopify-config.js': `/**
 * Shopify Storefront API Config
 *
 * 1. Go to your Shopify Admin → Settings → Apps and sales channels
 * 2. Click "Develop apps" → Create an app
 * 3. Under "Storefront API access scopes", enable:
 *    - unauthenticated_read_product_listings
 *    - unauthenticated_write_checkouts
 * 4. Copy the Storefront API access token and paste below.
 */

export const SHOPIFY_DOMAIN = 'YOUR_STORE.myshopify.com';
export const SHOPIFY_STOREFRONT_TOKEN = 'YOUR_PUBLIC_STOREFRONT_TOKEN';
`,

    'src/shopify.js': `/**
 * Shopify Storefront API client
 */

import { SHOPIFY_DOMAIN, SHOPIFY_STOREFRONT_TOKEN } from './shopify-config.js';

const STOREFRONT_API = \`https://\${SHOPIFY_DOMAIN}/api/2024-01/graphql.json\`;

async function sfetch(query, variables = {}) {
  const res = await fetch(STOREFRONT_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(\`Shopify API error: \${res.status}\`);
  const { data, errors } = await res.json();
  if (errors?.length) throw new Error(errors[0].message);
  return data;
}

export async function getProducts(first = 20) {
  const data = await sfetch(\`
    query Products($first: Int!) {
      products(first: $first) {
        edges { node {
          id title handle
          priceRange { minVariantPrice { amount currencyCode } }
          featuredImage { url altText }
          variants(first: 1) { edges { node { id availableForSale } } }
        }}
      }
    }
  \`, { first });
  return data.products.edges.map(e => e.node);
}

export async function getProductByHandle(handle) {
  const data = await sfetch(\`
    query Product($handle: String!) {
      product(handle: $handle) {
        id title handle descriptionHtml
        priceRange { minVariantPrice { amount currencyCode } }
        images(first: 5) { edges { node { url altText } } }
        variants(first: 10) {
          edges { node {
            id title availableForSale
            price { amount currencyCode }
          }}
        }
      }
    }
  \`, { handle });
  return data.product;
}

export async function createCheckout(lineItems) {
  const data = await sfetch(\`
    mutation CheckoutCreate($input: CheckoutCreateInput!) {
      checkoutCreate(input: $input) {
        checkout { webUrl }
        checkoutUserErrors { message }
      }
    }
  \`, {
    input: {
      lineItems: lineItems.map(({ variantId, quantity }) => ({ variantId, quantity })),
    },
  });
  const { checkout, checkoutUserErrors } = data.checkoutCreate;
  if (checkoutUserErrors?.length) throw new Error(checkoutUserErrors[0].message);
  return checkout.webUrl;
}

export function formatPrice(amount, currencyCode = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode.toUpperCase(),
  }).format(parseFloat(amount));
}
`,

    'src/cart.js': `/**
 * Cart — localStorage-backed shopping cart (shared across templates).
 */

const CART_KEY = 'cart';

export function getCart() {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || '[]'); }
  catch { return []; }
}

export function addToCart(item) {
  const cart = getCart();
  const existing = cart.find(i => i.id === item.id);
  if (existing) { existing.quantity = (existing.quantity || 1) + 1; }
  else { cart.push({ ...item, quantity: 1 }); }
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
}

export function removeFromCart(id) {
  localStorage.setItem(CART_KEY, JSON.stringify(getCart().filter(i => i.id !== id)));
}

export function clearCart() { localStorage.removeItem(CART_KEY); }

export function updateCartCount() {
  const count = getCart().reduce((sum, item) => sum + (item.quantity || 1), 0);
  document.querySelectorAll('#cart-count').forEach(el => {
    if (count > 0) { el.textContent = String(count); el.classList.remove('hidden'); }
    else { el.classList.add('hidden'); }
  });
}

export function formatPrice(cents, currency = 'USD') {
  if (cents == null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}
`,

    'src/navigation.js': `/**
 * Navigation utility for static sites.
 * Converts data-nav attributes to proper relative paths.
 */

export function resolvePath(siteAbsolutePath) {
  let target = siteAbsolutePath.replace(/^\\/+/, '');
  if (target.endsWith('/') || target === '') {
    target = target + 'index.html';
  }
  
  const currentPath = window.location.pathname.replace(/^\\/+/, '');
  const currentNormalized = currentPath === '' || currentPath === '/' ? 'index.html' : currentPath;
  const currentParts = currentNormalized.split('/');
  currentParts.pop();
  const depth = currentParts.filter(p => p).length;
  
  if (depth === 0) return target;
  return '../'.repeat(depth) + target;
}

export function initNavigation() {
  document.querySelectorAll('[data-nav]').forEach(link => {
    const siteAbsolutePath = link.getAttribute('data-nav');
    if (siteAbsolutePath) link.href = resolvePath(siteAbsolutePath);
  });
}

export function navigateTo(siteAbsolutePath) {
  window.location.href = resolvePath(siteAbsolutePath);
}
`,
  };
}
